import { baseApi } from './baseApi'
import { invalidateInOtherTabs } from 'store/crossTabInvalidation'

import {
  BillingPaymentDto,
  BillingPaymentsList,
  BillingPaymentsListQuery,
  CreateBillingPaymentDto,
  EraClaimPlanBalances,
  EraPostingPlan,
  ReleasedEraClaimsResult,
} from 'store/types'

type TransactionAddress = {
  transactionId?: string
  serviceLineIndex?: number
  transactionIndex?: number
}

type UpdateTransactionRequest = TransactionAddress & {
  paymentId: string
  patientId: number
  allowed?: number
  paidRefund?: number
  transfers?: number
  copay?: number
  writeOff?: number
  /** Required by the server whenever writeOff is above zero. */
  writeOffReason?: string | null
  /** The explanation the "Other" reason requires; saved as a note on the row. */
  writeOffNote?: string
  /** Changed fields of the payment's date, method and method details. */
  paymentDetails?: Record<string, string>
}

type ReverseTransactionRequest = TransactionAddress & {
  paymentId: string
  patientId: number
  note?: string
  reversalDate?: string
}

type ReversePaymentRequest = {
  paymentId: string
  patientId: number
  note?: string
  reversalDate?: string
}

type DeletePaymentRequest = {
  paymentId: string
  patientId: number
}

type WriteOffPatientBalanceRequest = {
  patientId: number
  chargeId: string
  cptCode: string
  /** Distinguishes duplicate-CPT lines (a two-night study bills a code twice). */
  startDos?: string | null
  amount: number
  reason: string
  /** Optional explanation; saved as a note on the row. */
  note?: string
}

// Backing out a payment reopens the denials it resolved.
const DENIAL_REOPEN_TAGS = ['BillingDenials', 'OrderCounts'] as const

// Reversing a payment, or deleting it, hands the remittance claims it was
// posted from back to the ERA pages as open work. Only those claims' posting
// plans are refetched — each one replays a charge.
const eraClaimReleaseTags = (releasedClaimIds: string[] = []) =>
  releasedClaimIds.length
    ? [
      'BillingTransactionClaims' as const,
      'BillingEraTransactions' as const,
      'BillingEra' as const,
      'EraExceptionQueue' as const,
      ...releasedClaimIds.map((id) => ({ type: 'EraPostingPlan' as const, id })),
    ]
    : []

// The ERA pages are often open in another tab while a payment is reversed on
// the patient's, and a tag invalidated here never reaches that tab's store.
const releaseInOtherTabs = async (
  _arg: unknown,
  { queryFulfilled }: { queryFulfilled: Promise<{ data: ReleasedEraClaimsResult }> },
): Promise<void> => {
  try {
    const { data } = await queryFulfilled
    const tags = eraClaimReleaseTags(data?.releasedClaimIds)

    if (tags.length) {
      invalidateInOtherTabs(tags)
    }
  } catch {
    // A failed mutation is reported where it was called.
  }
}

export const billingPaymentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createBillingPayment: builder.mutation<BillingPaymentDto, CreateBillingPaymentDto>({
      query: (body) => ({
        url: `/lab/patients/${body.patientId}/account/payments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, body) => [
        'BillingTransactionClaims',
        'BillingEraTransactions',
        'BillingEra',
        'BillingPatientPayments',
        'EraExceptionQueue',
        // A payer's posting resolves its open denials on the charge.
        'BillingDenials',
        { type: 'Patient', id: Number(body.patientId) },
        // Only the claim that was posted; its plan now says ALREADY_POSTED.
        ...(body.claimId ? [{ type: 'EraPostingPlan' as const, id: body.claimId }] : []),
      ],
    }),
    getBillingPayment: builder.query<BillingPaymentDto, { id: string; patientId: number }>({
      query: ({ id, patientId }) => ({ url: `/lab/patients/${patientId}/account/payments/${id}` }),
      providesTags: ['BillingPatientPayments'],
    }),
    getPatientBillingPayments: builder.query<BillingPaymentsList, BillingPaymentsListQuery>({
      query: ({ patientId, ...query }) => ({
        url: `/lab/patients/${patientId}/account/payments`,
        params: query,
      }),
      // BillingAttachments: the charge rows carry attachment counts, so an
      // upload or delete must refetch them.
      providesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        'BillingAttachments',
        { type: 'Patient', id: Number(patientId) },
      ],
    }),
    getEraPostingPlan: builder.query<EraPostingPlan, { eraId: string; claimId: string }>({
      query: ({ eraId, claimId }) => ({
        url: `/lab/billing/era-reports/${eraId}/claims/${claimId}/posting-plan`,
      }),
      // Tagged per claim, not with the listing's tag: under that tag every
      // posting re-fetched the plan of every open row on the page, tens of
      // thousands of requests a day, each one replaying a charge.
      providesTags: (result, error, { claimId }) => [{ type: 'EraPostingPlan', id: claimId }],
    }),
    getEraClaimPlanBalances: builder.query<EraClaimPlanBalances, { eraId: string; claimId: string; chargeId: string }>({
      query: ({ eraId, claimId, chargeId }) => ({
        url: `/lab/billing/era-reports/${eraId}/claims/${claimId}/plan-balances`,
        params: { chargeId },
      }),
    }),
    updatePaymentTransaction: builder.mutation<void, UpdateTransactionRequest>({
      query: ({ paymentId, patientId, ...body }) => ({
        url: `/lab/patients/${patientId}/account/payments/${paymentId}/transaction`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
      ],
    }),
    reversePaymentTransaction: builder.mutation<void, ReverseTransactionRequest>({
      query: ({ paymentId, patientId, ...body }) => ({
        url: `/lab/patients/${patientId}/account/payments/${paymentId}/transaction/reverse`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
        ...DENIAL_REOPEN_TAGS,
      ],
    }),
    // Every row of the payment at once — the only way an ERA posting is
    // reversed, and what returns its claim to the ERA pages.
    reverseBillingPayment: builder.mutation<ReleasedEraClaimsResult, ReversePaymentRequest>({
      query: ({ paymentId, patientId, ...body }) => ({
        url: `/lab/patients/${patientId}/account/payments/${paymentId}/reverse`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
        ...eraClaimReleaseTags(result?.releasedClaimIds),
        ...DENIAL_REOPEN_TAGS,
      ],
      onQueryStarted: releaseInOtherTabs,
    }),
    writeOffPatientBalance: builder.mutation<void, WriteOffPatientBalanceRequest>({
      query: ({ patientId, chargeId, ...body }) => ({
        url: `/lab/patients/${patientId}/account/charges/${chargeId}/patient-write-off`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
      ],
    }),
    deletePayment: builder.mutation<ReleasedEraClaimsResult, DeletePaymentRequest>({
      query: ({ paymentId, patientId }) => ({
        url: `/lab/patients/${patientId}/account/payments/${paymentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
        ...eraClaimReleaseTags(result?.releasedClaimIds),
        ...DENIAL_REOPEN_TAGS,
      ],
      onQueryStarted: releaseInOtherTabs,
    }),
    unapplyBillingPaymentApplication: builder.mutation<
      void,
      { patientId: number; applicationId: string; note?: string }
    >({
      query: ({ patientId, applicationId, note }) => ({
        url: `/lab/patients/${patientId}/account/payments/applications/${applicationId}/unapply`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: (result, error, { patientId }) => [
        'BillingPatientPayments',
        { type: 'Patient', id: Number(patientId) },
      ],
    }),
  }),
})

export const {
  useCreateBillingPaymentMutation,
  useGetBillingPaymentQuery,
  useGetPatientBillingPaymentsQuery,
  useGetEraPostingPlanQuery,
  useGetEraClaimPlanBalancesQuery,
  useUpdatePaymentTransactionMutation,
  useReverseBillingPaymentMutation,
  useReversePaymentTransactionMutation,
  useWriteOffPatientBalanceMutation,
  useDeletePaymentMutation,
  useUnapplyBillingPaymentApplicationMutation,
} = billingPaymentsApi
